import React, { useEffect, useState, useCallback, useMemo } from "react";
import renameEngineApi, { dropboxFileUrl } from "../api/renameEngine";
import FloatingPdfViewer from "../components/FloatingPdfViewer";
import { dragOutProps, contextShareProps } from "../utils/dragOut";
import "./RenameEnginePage.css";

const DEFAULT_FOLDER = "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch";
// Welle 15: Korrektur-Inputs pro Feld sind komplett weg — der Filename + die
// Schema-Auswahl sind die einzigen User-Eingaben. Backend leitet alle
// Feld-Werte server-seitig aus dem Filename ab.

function _pillColor(source) {
  if (!source) return ["#9aa4b8", "#1a2030"];
  if (source.startsWith("db:")) return ["#9be09b", "rgba(80,200,120,0.18)"];
  if (source.startsWith("pattern:")) return ["#9bcaff", "rgba(80,140,220,0.18)"];
  if (source.startsWith("analysis_match")) return ["#e3c574", "rgba(200,160,80,0.18)"];
  if (source === "free_tail") return ["#cdd3e0", "rgba(180,180,200,0.15)"];
  if (source.startsWith("position:")) return ["#e3c574", "rgba(200,160,80,0.18)"];
  return ["#ff9090", "rgba(220,80,80,0.18)"];
}

export default function RenameEnginePage() {
  const [status, setStatus] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [error, setError] = useState(null);

  // Ordner-Browser-State
  const [browsePath, setBrowsePath] = useState(""); // aktueller Pfad im Browser
  const [browseContents, setBrowseContents] = useState(null); // {subfolders, files, folder}
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState(null);

  // files = ausgewählter Ordner (PDFs laden für Batch/Listen)
  const [files, setFiles] = useState(null);
  const [listing, setListing] = useState(false);
  const [listError, setListError] = useState(null);

  // Inline-Analyse-State pro Datei: pathLower → {loading, analysis, targetName, gold, learnLoading, learnResult, learnError}
  const [inlineMap, setInlineMap] = useState({});
  const [expandedPath, setExpandedPath] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [analyzedName, setAnalyzedName] = useState(null);

  // Gold-/Training-State
  const [goldStats, setGoldStats] = useState(null);
  const [uploadSchemaOverride, setUploadSchemaOverride] = useState("");
  const [training, setTraining] = useState(false);
  const [trainResult, setTrainResult] = useState(null);
  const [trainError, setTrainError] = useState(null);

  // Lern-/Re-Analyse-State
  const [lastSource, setLastSource] = useState(null); // {type:'upload',file} | {type:'dropbox',path,name}
  const [targetName, setTargetName] = useState("");
  const [learning, setLearning] = useState(false);
  const [learnResult, setLearnResult] = useState(null);
  const [learnError, setLearnError] = useState(null);

  // Batch-Analyse-State
  const [batchSelected, setBatchSelected] = useState(new Set());
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);

  // Floating PDF-Viewer: {url?, file?, name?} | null
  const [viewerSource, setViewerSource] = useState(null);

  // Welle 15: Schema-Dropdown-Quelle + per-Datei Live-Preview.
  const [schemaList, setSchemaList] = useState([]);

  const loadStatus = useCallback(async () => {
    setError(null);
    try {
      const [st, cat] = await Promise.all([
        renameEngineApi.status(),
        renameEngineApi.catalogsSummary(),
      ]);
      setStatus(st);
      setCatalogs(cat);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, []);

  const loadGoldStats = useCallback(async () => {
    try {
      const res = await renameEngineApi.goldList(500);
      setGoldStats(res);
    } catch (_) {
      /* nicht fatal */
    }
  }, []);

  useEffect(() => { loadStatus(); loadGoldStats(); }, [loadStatus, loadGoldStats]);

  // Schema-Dropdown einmalig beim Mount laden.
  useEffect(() => {
    renameEngineApi.schemasList()
      .then((d) => setSchemaList(d?.schemas || []))
      .catch(() => setSchemaList([]));
  }, []);

  // Welle 15: 300 ms debounced Live-Preview pro Inline-Datei.
  // Reagiert auf targetName und schemaOverride. Speichert Response in
  // inlineMap[key].parsePreview.
  useEffect(() => {
    const timers = {};
    Object.entries(inlineMap).forEach(([key, item]) => {
      const sha = item?.analysis?.sha256;
      const fn = item?.targetName;
      const schemaOv = item?.schemaOverride || item?.analysis?.schema?.code;
      if (!sha || !fn) return;
      const lastKey = `${sha}|${fn}|${schemaOv || ""}`;
      if (item.parsePreviewKey === lastKey) return;
      timers[key] = setTimeout(async () => {
        try {
          const res = await renameEngineApi.parseFilename({
            sha256: sha, filename: fn, schema: schemaOv || undefined,
          });
          setInlineMap((m) => ({
            ...m,
            [key]: { ...(m[key] || {}), parsePreview: res, parsePreviewKey: lastKey },
          }));
        } catch (_) {
          /* still — Vorschau ist nicht kritisch */
        }
      }, 300);
    });
    return () => Object.values(timers).forEach((t) => clearTimeout(t));
  }, [inlineMap]);

  // Ordner-Browser: Inhalt eines Pfades laden (Unterordner + PDFs)
  const browseFolder = useCallback(async (path) => {
    setBrowseLoading(true); setBrowseError(null);
    try {
      const res = await renameEngineApi.dropboxBrowse(path);
      setBrowsePath(path);
      setBrowseContents(res);
      // Automatisch PDFs im aktuellen Ordner als File-Liste setzen
      if (res.files && res.files.length > 0) {
        setFiles({ folder: path, count: res.files.length, files: res.files });
        setInlineMap({});
        setExpandedPath(null);
        setBatchSelected(new Set());
      } else {
        setFiles(null);
      }
    } catch (e) {
      setBrowseError(e.message || String(e));
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  // Beim ersten Laden DEFAULT_FOLDER öffnen (realer Dropbox-Pfad, kein Alias)
  useEffect(() => { browseFolder(DEFAULT_FOLDER); }, [browseFolder]);

  // Breadcrumb-Segmente aus Pfad ableiten
  const breadcrumbs = useMemo(() => {
    const parts = browsePath.split("/").filter(Boolean);
    const crumbs = [{ label: "Dropbox", path: "" }];
    let cur = "";
    for (const p of parts) {
      cur = cur + "/" + p;
      crumbs.push({ label: p, path: cur });
    }
    return crumbs;
  }, [browsePath]);

  const listFolder = async () => {
    if (!browsePath && !browseContents) return;
    setListing(true); setListError(null); setFiles(null);
    try {
      const res = await renameEngineApi.dropboxList(browsePath, false);
      setFiles(res);
    } catch (e) {
      setListError(e.message || String(e));
    } finally {
      setListing(false);
    }
  };

  // Lädt eine Datei und versucht Vor-Befüllung des Gold-Inputs.
  const runAnalysis = async (promise, name, source = null) => {
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalysis(null);
    setAnalyzedName(name);
    setLearnResult(null);
    setLearnError(null);
    setUploadSchemaOverride("");
    if (source) setLastSource(source);
    try {
      const result = await promise;
      setAnalysis(result);
      setTargetName(result?.proposal?.filename || "");
    } catch (e) {
      setAnalyzeError(e.message || String(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const onUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setViewerSource({ file, name: file.name });
      runAnalysis(renameEngineApi.analyze(file), file.name,
        { type: "upload", file });
    }
    e.target.value = "";
  };

  // Inline-Analyse-Helpers
  const updateInline = (key, updates) =>
    setInlineMap((m) => ({ ...m, [key]: { ...(m[key] || {}), ...updates } }));

  const clickFileInline = async (f) => {
    const key = f.path_lower;
    const path = f.path_display || f.path_lower;
    // Viewer: auf die geklickte Datei wechseln (auch wenn schon expanded),
    // damit man im offenen Floating-Panel zwischen Dateien navigieren kann.
    // Nur ändern, wenn es wirklich eine andere Datei ist (sonst unnötiger Reload).
    const newUrl = dropboxFileUrl(path);
    setViewerSource((prev) => (prev && prev.url === newUrl ? prev : { url: newUrl, name: f.name }));
    if (expandedPath === key) { setExpandedPath(null); return; }
    setExpandedPath(key);
    if (inlineMap[key]?.analysis || inlineMap[key]?.loading) return;
    updateInline(key, { loading: true, learnError: null, sourcePath: path });
    try {
      const result = await renameEngineApi.analyzeDropbox(path);
      updateInline(key, {
        loading: false, analysis: result, sourcePath: path,
        targetName: result?.proposal?.filename || "", gold: {},
      });
    } catch (e) {
      updateInline(key, { loading: false, learnError: e.message || String(e) });
    }
  };

  // Erneute Analyse derselben Dropbox-Datei zur Verifikation des Lernens.
  // Ersetzt inline.analysis, lässt learnResult sichtbar als Vergleichs-Anker.
  const reanalyzeInline = async (pathLower) => {
    const item = inlineMap[pathLower];
    const path = item?.sourcePath;
    if (!path) return;
    updateInline(pathLower, { reanalyzing: true, learnError: null });
    try {
      const result = await renameEngineApi.analyzeDropbox(path);
      updateInline(pathLower, {
        reanalyzing: false,
        analysis: result,
        targetName: result?.proposal?.filename || item.targetName || "",
        reanalyzed: true,
      });
    } catch (e) {
      updateInline(pathLower, { reanalyzing: false, learnError: e.message || String(e) });
    }
  };

  // Datei in Dropbox umbenennen (gleicher Ordner, neuer Dateiname aus targetName).
  // Bei autorename=true hängt Dropbox bei Konflikt automatisch "(1)" an.
  const renameInline = async (pathLower, autorename = false) => {
    const item = inlineMap[pathLower];
    const path = item?.sourcePath;
    const newName = (item?.targetName || "").trim();
    if (!path || !newName) return;
    updateInline(pathLower, { renaming: true, renameError: null, renameConflict: false });
    try {
      const res = await renameEngineApi.dropboxRename({
        source_path: path, new_name: newName, autorename,
      });
      const newPath = res.path_display || res.path_lower;
      // Browser-Liste: das alte Datei-Item aktualisieren.
      setBrowseContents((bc) => {
        if (!bc) return bc;
        return {
          ...bc,
          files: (bc.files || []).map((f) =>
            f.path_lower === pathLower
              ? { ...f, name: res.name || newName,
                  path_display: newPath, path_lower: (res.path_lower || newPath).toLowerCase() }
              : f
          ),
        };
      });
      // Inline-State neu verschlüsseln auf den neuen Pfad.
      const newKey = (res.path_lower || newPath).toLowerCase();
      setInlineMap((m) => {
        const next = { ...m };
        const old = next[pathLower];
        delete next[pathLower];
        next[newKey] = {
          ...(old || {}),
          renaming: false, renameError: null, renamed: true,
          sourcePath: newPath,
        };
        return next;
      });
      if (expandedPath === pathLower) setExpandedPath(newKey);
      // Viewer: auf neuen Pfad umstellen.
      setViewerSource({ url: dropboxFileUrl(newPath), name: res.name || newName });
    } catch (e) {
      updateInline(pathLower, {
        renaming: false,
        renameError: e.message || String(e),
        renameConflict: e?.status === 409,
      });
    }
  };

  const learnInline = async (pathLower) => {
    const item = inlineMap[pathLower];
    if (!item?.analysis?.sha256) return;
    updateInline(pathLower, { learnLoading: true, learnError: null, learnResult: null });
    // Welle 15: Backend klassifiziert den Filename selbst — wir senden nur
    // sha256 + filename + schema-Override aus dem Dropdown. Keine corrected-
    // Dicts mehr aus dem Frontend.
    const filename = item.targetName || item.analysis.filename;
    const schema = item.schemaOverride || item.analysis?.schema?.code;
    try {
      const res = await renameEngineApi.learn({
        sha256: item.analysis.sha256,
        filename, schema,
      });
      // Für Diff nach Re-Analyse merken wir uns die zuletzt vom Server
      // klassifizierten Felder (aus der Live-Preview).
      const learnedFields = item.parsePreview?.fields_classified
        ? Object.fromEntries(Object.entries(item.parsePreview.fields_classified)
            .map(([k, v]) => [k, v?.value]))
        : {};
      updateInline(pathLower, {
        learnLoading: false, learnResult: res, learnedFields,
      });
      loadGoldStats();
    } catch (e) {
      updateInline(pathLower, { learnLoading: false, learnError: e.message || String(e) });
    }
  };

  // Batch: Auswahl umschalten
  const toggleBatchSelect = (pathLower) => {
    setBatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pathLower)) next.delete(pathLower);
      else next.add(pathLower);
      return next;
    });
  };

  // Batch: ausgewählte Dateien in die Queue aufnehmen und sequenziell analysieren
  const addAndAnalyzeBatch = async () => {
    if (!browseContents?.files || batchSelected.size === 0 || batchRunning) return;
    const selectedFiles = browseContents.files.filter((f) => batchSelected.has(f.path_lower));
    const existingPaths = new Set(batchQueue.map((it) => it.file.path_lower));
    const newItems = selectedFiles
      .filter((f) => !existingPaths.has(f.path_lower))
      .map((f) => ({
        file: f, status: "pending", analysis: null,
        targetName: "", learning: false, learnResult: null, learnError: null,
      }));
    if (newItems.length === 0) { setBatchSelected(new Set()); return; }
    const allItems = [...batchQueue, ...newItems];
    setBatchQueue(allItems);
    setBatchRunning(true);
    setBatchSelected(new Set());
    for (const item of newItems) {
      const idx = allItems.findIndex((it) => it.file.path_lower === item.file.path_lower);
      setBatchQueue((q) => q.map((it, i) => i === idx ? { ...it, status: "analyzing" } : it));
      try {
        const path = item.file.path_display || item.file.path_lower;
        const result = await renameEngineApi.analyzeDropbox(path);
        setBatchQueue((q) => q.map((it, i) =>
          i === idx ? { ...it, status: "done", analysis: result,
            targetName: result?.proposal?.filename || "" } : it
        ));
      } catch (e) {
        setBatchQueue((q) => q.map((it, i) =>
          i === idx ? { ...it, status: "error", learnError: e.message || String(e) } : it
        ));
      }
    }
    setBatchRunning(false);
  };

  // Batch: Einzelner Lernschritt (item + stabiler idx aus Snapshot)
  const learnBatchItem = async (item, idx) => {
    if (!item.analysis?.sha256) return;
    setBatchQueue((q) => q.map((it, i) =>
      i === idx ? { ...it, learning: true, learnResult: null, learnError: null } : it));
    // Welle 15: Batch nutzt denselben Filename-only-Flow wie der Inline-Pfad.
    const filename = item.targetName || item.analysis.filename;
    const schema = item.analysis?.schema?.code;
    try {
      const res = await renameEngineApi.learn({
        sha256: item.analysis.sha256,
        filename, schema,
      });
      setBatchQueue((q) => q.map((it, i) =>
        i === idx ? { ...it, learning: false, learnResult: res } : it));
      loadGoldStats();
    } catch (e) {
      setBatchQueue((q) => q.map((it, i) =>
        i === idx ? { ...it, learning: false, learnError: e.message || String(e) } : it));
    }
  };

  // Batch: Alle analysierten Items ohne Lernergebnis lernen
  const learnAllBatch = async () => {
    if (batchRunning) return;
    const snapshot = [...batchQueue];
    for (let i = 0; i < snapshot.length; i++) {
      const item = snapshot[i];
      if (item.status === "done" && !item.learnResult && !item.learning)
        await learnBatchItem(item, i);
    }
  };

  // Erneut analysieren (gleiche Quelle) — zeigt angewandte gelernte Overrides.
  const reanalyze = async () => {
    if (!lastSource) return;
    if (lastSource.type === "upload") {
      await runAnalysis(renameEngineApi.analyze(lastSource.file),
        lastSource.file.name, lastSource);
    } else {
      await runAnalysis(renameEngineApi.analyzeDropbox(lastSource.path),
        lastSource.name, lastSource);
    }
  };

  const confClass = (c) => (c >= 0.8 ? "ok" : c >= 0.5 ? "warn" : "bad");

  const runLearn = async () => {
    if (!analysis?.sha256) {
      setLearnError("Keine Analyse vorhanden.");
      return;
    }
    setLearning(true); setLearnError(null); setLearnResult(null);
    try {
      const res = await renameEngineApi.learn({
        sha256: analysis.sha256,
        filename: targetName || analysis.filename,
        schema: uploadSchemaOverride || analysis?.schema?.code,
      });
      setLearnResult(res);
      loadGoldStats();
      // Erneut analysieren: zeigt die jetzt angewandten gelernten Overrides.
      await reanalyze();
    } catch (e) {
      setLearnError(e.message || String(e));
    } finally {
      setLearning(false);
    }
  };

  const runTraining = async () => {
    setTraining(true); setTrainError(null); setTrainResult(null);
    try {
      const res = await renameEngineApi.trainFromGold(5);
      setTrainResult(res);
    } catch (e) {
      setTrainError(e.message || String(e));
    } finally {
      setTraining(false);
    }
  };

  return (
    <div className="re-page">
      <FloatingPdfViewer source={viewerSource} onClose={() => setViewerSource(null)} />
      <header className="re-header">
        <h1>Rename-Engine <span className="re-badge">2.0</span></h1>
        <p className="re-sub">
          Dokument wählen → Namen prüfen/korrigieren → Lernen.
          Das Altsystem bleibt unberührt.
        </p>
      </header>

      {error && <div className="re-alert re-alert-error">Status-Fehler: {error}</div>}

      {/* ── HAUPT-WORKFLOW: Finder-Browser + Inline-Analyse ── */}
      <section className="re-card re-filebrowser-card">
        <div className="re-card-header-row">
          <h2>📂 Dokumente</h2>
          <label className="re-btn secondary re-upload" style={{ fontSize: 13, padding: "6px 14px" }}>
            ⬆ PDF hochladen…
            <input type="file" accept="application/pdf" onChange={onUpload} hidden />
          </label>
        </div>

        {/* Upload-Ergebnis */}
        {(analyzing || (analysis && lastSource?.type === "upload")) && (
          <div className="re-inline-panel" style={{ marginBottom: 12 }}>
            {analyzing && <p className="re-muted">Analysiere {analyzedName}…</p>}
            {analyzeError && <div className="re-alert re-alert-error">{analyzeError}</div>}
            {analysis && !analyzing && (
              <>
                <div className="re-inline-meta">
                  <b style={{ color: "#fff" }}>{analyzedName}</b>
                  {analysis.schema?.code && <span className="re-schema-code">{analysis.schema.code}</span>}
                  <span className={`re-conf ${confClass(analysis.schema?.confidence || 0)}`}>
                    {((analysis.schema?.confidence || 0) * 100).toFixed(0)}%
                  </span>
                  <span className="re-muted">· {analysis.pages} S. · {analysis.ocr_used ? "OCR" : "digital"} · {analysis.elapsed_ms}ms</span>
                </div>
                <div className="re-inline-name-row" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <label className="re-inline-label">Dateiname</label>
                  <input className="re-input re-filename-input"
                    style={{ flex: "1 1 360px", minWidth: 240 }}
                    value={targetName}
                    placeholder="PREFIX - YYYY-MM-DD - Absender - … - SuffixOptional.pdf"
                    onChange={(e) => setTargetName(e.target.value)} />
                  <label className="re-inline-label" style={{ marginLeft: 12 }}>Schema</label>
                  <select
                    className="re-input"
                    style={{ minWidth: 160 }}
                    value={uploadSchemaOverride || analysis.schema?.code || ""}
                    onChange={(e) => setUploadSchemaOverride(e.target.value)}
                  >
                    {!(analysis.schema?.code) && <option value="">— wählen —</option>}
                    {schemaList.map((s) => (
                      <option key={s.code} value={s.code}>{s.code}</option>
                    ))}
                  </select>
                </div>
                <div className="re-inline-actions">
                  <button className="re-btn success" onClick={runLearn} disabled={learning}>
                    {learning ? "lerne…" : "✓ Korrekt & lernen"}
                  </button>
                  <button className="re-btn secondary" onClick={() => { setAnalysis(null); setLearnResult(null); setLearnError(null); }}>✕</button>
                </div>
                {learnResult && <div className="re-alert re-alert-success" style={{ marginTop: 8 }}>
                  ✓ {learnResult.gold_saved} Wert(e) gelernt{learnResult.learned?.length > 0 && <> · {learnResult.learned.map((l) => l.field).join(", ")}</>}
                </div>}
                {learnError && <div className="re-alert re-alert-error" style={{ marginTop: 8 }}>{learnError}</div>}
              </>
            )}
          </div>
        )}

        {/* Breadcrumb-Navigation */}
        <div className="re-breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.path}>
              {i > 0 && <span className="re-breadcrumb-sep">›</span>}
              <button
                className={`re-breadcrumb-btn${i === breadcrumbs.length - 1 ? " active" : ""}`}
                onClick={() => browseFolder(crumb.path)}
                disabled={browseLoading}
              >
                {i === 0 ? "🗄 Dropbox" : crumb.label}
              </button>
            </span>
          ))}
          {browseLoading && <span className="re-muted" style={{ marginLeft: 8, fontSize: 12 }}>lädt…</span>}
          <button
            className="re-btn secondary"
            onClick={async () => {
              try {
                await renameEngineApi.catalogsReload();
              } catch (_) {}
              browseFolder(browsePath);
            }}
            disabled={browseLoading}
            title="Stammdaten + Datei-Liste neu laden (nach Eintrag im Admin)"
            style={{ marginLeft: "auto", padding: "3px 10px", fontSize: 12 }}
          >
            🔄
          </button>
        </div>
        {browseError && <div className="re-alert re-alert-error">{browseError}</div>}

        {/* Batch-Aktions-Leiste */}
        {batchSelected.size > 0 && (
          <div className="re-batch-bar">
            <span className="re-batch-bar-info">
              {batchSelected.size} PDF{batchSelected.size > 1 ? "s" : ""} ausgewählt
            </span>
            <button className="re-btn success" onClick={addAndAnalyzeBatch} disabled={batchRunning}>
              {batchRunning ? "analysiere…" : "Alle analysieren & in Batch"}
            </button>
            <button className="re-btn secondary" onClick={() => setBatchSelected(new Set())}>
              Auswahl aufheben
            </button>
          </div>
        )}

        {/* ── FINDER-LISTE ── */}
        <div className="re-finder">
          {/* Spalten-Header */}
          <div className="re-finder-header">
            <span className="re-finder-col-name">Name</span>
            <span className="re-finder-col-size">Größe</span>
            <span className="re-finder-col-proposal">Vorgeschlagener Name</span>
          </div>

          {browseLoading && (
            <div className="re-finder-empty">lädt…</div>
          )}

          {browseContents && !browseLoading && (
            <>
              {/* Übergeordneter Ordner (außer Root) */}
              {browsePath && browsePath !== "/" && (
                <div
                  className="re-finder-row re-finder-folder re-finder-parent"
                  onClick={() => {
                    const parent = browsePath.substring(0, browsePath.lastIndexOf("/")) || "";
                    browseFolder(parent);
                  }}
                >
                  <span className="re-finder-col-name">
                    <span className="re-finder-icon">↑</span>
                    <span className="re-finder-name re-muted">..</span>
                  </span>
                  <span className="re-finder-col-size"></span>
                  <span className="re-finder-col-proposal"></span>
                </div>
              )}

              {/* Unterordner */}
              {browseContents.subfolders.map((sf) => (
                <div
                  key={sf.path_lower}
                  className="re-finder-row re-finder-folder"
                  onClick={() => browseFolder(sf.path_display || sf.path_lower)}
                >
                  <span className="re-finder-col-name">
                    <span className="re-finder-icon">📁</span>
                    <span className="re-finder-name">{sf.name}</span>
                  </span>
                  <span className="re-finder-col-size"></span>
                  <span className="re-finder-col-proposal">
                    <span className="re-finder-arrow">›</span>
                  </span>
                </div>
              ))}

              {/* PDFs */}
              {browseContents.files.map((f) => {
                const key = f.path_lower;
                const inline = inlineMap[key] || {};
                const isExpanded = expandedPath === key;
                const isDone = !!inline.learnResult;
                const isChecked = batchSelected.has(key);
                const proposal = inline.analysis?.proposal?.filename;
                const needsReview = inline.analysis?.proposal?.needs_review;

                return (
                  <div key={key} className={`re-finder-entry${isExpanded ? " expanded" : ""}${isDone ? " done" : ""}`}>
                    <div
                      className={`re-finder-row re-finder-file${isChecked ? " checked" : ""}`}
                      onClick={() => clickFileInline(f)}
                    >
                      <span className="re-finder-col-name">
                        <input
                          type="checkbox"
                          className="re-finder-check"
                          checked={isChecked}
                          onChange={() => toggleBatchSelect(key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="re-finder-icon">
                          {inline.loading ? "⏳" : isDone ? "✅" : "📄"}
                        </span>
                        <span className="re-finder-name"
                          {...dragOutProps(f.path_display || f.path_lower, f.name)}
                          {...contextShareProps(f.path_display || f.path_lower, f.name)}
                        >{f.name}</span>
                      </span>
                      <span className="re-finder-col-size">{(f.size / 1024).toFixed(0)} KB</span>
                      <span className="re-finder-col-proposal">
                        {proposal && !isDone && (
                          <span className={`re-finder-proposal${needsReview ? " warn" : ""}`}>
                            {proposal.replace(/\.pdf$/i, "")}
                          </span>
                        )}
                        {isDone && <span className="re-done-badge">✓ gelernt</span>}
                        {inline.loading && <span className="re-muted" style={{ fontSize: 12 }}>analysiere…</span>}
                        <span className="re-finder-toggle">{isExpanded ? "▾" : "▸"}</span>
                      </span>
                    </div>

                    {/* Inline-Analyse-Panel */}
                    {isExpanded && (
                      <div className="re-inline-panel">
                        {inline.loading && <p className="re-muted" style={{ margin: "8px 0" }}>Analysiere Dokument…</p>}
                        {!inline.loading && !inline.analysis && inline.learnError && (
                          <div className="re-alert re-alert-error">{inline.learnError}</div>
                        )}
                        {inline.analysis && (
                          <>
                            <div className="re-inline-meta">
                              {inline.analysis.schema?.code && <span className="re-schema-code">{inline.analysis.schema.code}</span>}
                              <span className={`re-conf ${confClass(inline.analysis.schema?.confidence || 0)}`}>
                                {((inline.analysis.schema?.confidence || 0) * 100).toFixed(0)}%
                              </span>
                              <span className="re-muted">
                                · {inline.analysis.pages} S. · {inline.analysis.ocr_used ? "OCR" : "digital"} · {inline.analysis.elapsed_ms}ms
                              </span>
                              {inline.analysis.learned?.length > 0 && (
                                <span className="re-done-badge" style={{ marginLeft: 8 }}>
                                  {inline.analysis.learned.length} Override(s) angewandt
                                </span>
                              )}
                            </div>
                            <div className="re-inline-name-row" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <label className="re-inline-label">Dateiname</label>
                              <input
                                className="re-input re-filename-input"
                                style={{ flex: "1 1 360px", minWidth: 240 }}
                                value={inline.targetName || ""}
                                placeholder="PREFIX - YYYY-MM-DD - Absender - … - SuffixOptional.pdf"
                                onChange={(e) => updateInline(key, { targetName: e.target.value })}
                              />
                              <label className="re-inline-label" style={{ marginLeft: 12 }}>Schema</label>
                              <select
                                className="re-input"
                                style={{ minWidth: 160 }}
                                value={inline.schemaOverride || inline.analysis.schema?.code || ""}
                                onChange={(e) => updateInline(key, { schemaOverride: e.target.value })}
                              >
                                {!(inline.analysis.schema?.code) && <option value="">— wählen —</option>}
                                {schemaList.map((s) => (
                                  <option key={s.code} value={s.code}>{s.code}</option>
                                ))}
                              </select>
                            </div>
                            {/* Welle 15: Live-Preview, was das Backend aus dem Filename liest. */}
                            {inline.parsePreview && (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0", fontSize: 11 }}>
                                <span className="re-muted" style={{ fontSize: 11, alignSelf: "center" }}>wird gelernt als:</span>
                                {Object.entries(inline.parsePreview.fields_classified || {}).map(([f, info]) => {
                                  const [color, bg] = _pillColor(info.source);
                                  const posInfo = inline.parsePreview.position_anchors?.[f];
                                  return (
                                    <span key={f}
                                      title={(info.source || "") + (info.matched ? ` · ${info.matched}` : "")
                                             + (posInfo?.label_before ? ` · neben ${posInfo.label_before}` : "")
                                             + (posInfo?.page !== undefined ? ` · S.${(posInfo.page || 0) + 1}` : "")}
                                      style={{ padding: "2px 6px", borderRadius: 4, background: bg, color, border: `1px solid ${color}` }}>
                                      <b>{f}</b>={info.value}
                                    </span>
                                  );
                                })}
                                {inline.parsePreview.free_tail && (() => {
                                  const [color, bg] = _pillColor("free_tail");
                                  return (
                                    <span title="Frei vergebener Suffix — wird im neuen Filename behalten"
                                      style={{ padding: "2px 6px", borderRadius: 4, background: bg, color, border: `1px solid ${color}` }}>
                                      <b>free_tail</b>=&quot;{inline.parsePreview.free_tail}&quot;
                                    </span>
                                  );
                                })()}
                              </div>
                            )}
                            <div className="re-inline-actions">
                              <button className="re-btn success" onClick={() => learnInline(key)} disabled={inline.learnLoading}
                                title="Filename + Schema als Wahrheit speichern, Anker + Filename-Form lernen">
                                {inline.learnLoading ? "lerne…" : "✓ Korrekt & lernen"}
                              </button>
                              <button
                                className="re-btn"
                                onClick={() => renameInline(key)}
                                disabled={inline.renaming || !inline.targetName}
                                title="Datei in Dropbox auf den oben stehenden Dateinamen umbenennen"
                              >
                                {inline.renaming ? "benenne um…" : "📂 In Dropbox umbenennen"}
                              </button>
                              <button
                                className="re-btn"
                                onClick={() => reanalyzeInline(key)}
                                disabled={inline.reanalyzing}
                                title="PDF erneut analysieren"
                              >
                                {inline.reanalyzing ? "analysiere…" : "🔄 Neu analysieren"}
                              </button>
                              <button className="re-btn secondary" onClick={() => setExpandedPath(null)}>✕</button>
                            </div>
                            {inline.renamed && (
                              <div className="re-alert re-alert-success" style={{ marginTop: 8 }}>
                                ✓ in Dropbox umbenannt
                              </div>
                            )}
                            {inline.renameError && !inline.renameConflict && (
                              <div className="re-alert re-alert-error" style={{ marginTop: 8 }}>
                                Rename fehlgeschlagen: {inline.renameError}
                              </div>
                            )}
                            {inline.renameConflict && (
                              <div className="re-alert re-alert-error" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                <span style={{ flex: "1 1 auto" }}>
                                  Eine Datei mit diesem Namen existiert bereits.
                                </span>
                                <button
                                  className="re-btn"
                                  onClick={() => renameInline(key, true)}
                                  disabled={inline.renaming}
                                  title="Dropbox hängt automatisch (1)/(2)… an"
                                  style={{ padding: "4px 10px", fontSize: 12 }}
                                >
                                  {inline.renaming ? "benenne um…" : "Trotzdem — mit Suffix"}
                                </button>
                              </div>
                            )}
                            {inline.learnResult && (
                              <div className="re-alert re-alert-success" style={{ marginTop: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                  <span style={{ flex: "1 1 auto" }}>
                                    ✓ {inline.learnResult.gold_saved} Wert(e) gelernt
                                    {inline.learnResult.learned?.length > 0 && <> · verallgemeinert: {inline.learnResult.learned.map((l) => l.field).join(", ")}</>}
                                    {inline.reanalyzed && <> · <b style={{ color: "#9be09b" }}>erneut geprüft</b></>}
                                  </span>
                                  <button
                                    className="re-btn"
                                    onClick={() => reanalyzeInline(key)}
                                    disabled={inline.reanalyzing}
                                    title="Erneut analysieren, um die gelernten Werte zu verifizieren"
                                    style={{ padding: "4px 10px", fontSize: 12 }}
                                  >
                                    {inline.reanalyzing ? "prüfe…" : (inline.reanalyzed ? "🔄 nochmal prüfen" : "🔄 Erneut analysieren — prüfen")}
                                  </button>
                                </div>
                                {inline.reanalyzed && inline.learnedFields && (
                                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
                                    {Object.entries(inline.learnedFields).map(([f, want]) => {
                                      const got = f === "schema"
                                        ? (inline.analysis?.schema?.code || "")
                                        : (inline.analysis?.fields?.[f]?.value || "");
                                      const match = String(got).trim() === String(want).trim();
                                      return (
                                        <span key={f}
                                          title={match ? `gelernt: ${want}` : `gelernt: ${want} · jetzt: ${got || "—"}`}
                                          style={{
                                            padding: "2px 6px", borderRadius: 4,
                                            background: match ? "rgba(80,200,120,0.18)" : "rgba(255,120,120,0.18)",
                                            color: match ? "#9be09b" : "#ff9090",
                                            border: `1px solid ${match ? "rgba(80,200,120,0.35)" : "rgba(255,120,120,0.35)"}`,
                                          }}>
                                          {match ? "✓" : "✗"} {f}: {match ? want : `${want} → ${got || "—"}`}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                            {inline.learnError && !inline.loading && (
                              <div className="re-alert re-alert-error" style={{ marginTop: 8 }}>{inline.learnError}</div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {browseContents.subfolders.length === 0 && browseContents.files.length === 0 && (
                <div className="re-finder-empty">Dieser Ordner enthält keine Ordner oder PDFs</div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── BATCH-QUEUE ── */}
      {batchQueue.length > 0 && (
        <section className="re-card">
          <div className="re-batch-header">
            <h2>Batch ({batchQueue.length})</h2>
            <div className="re-row">
              <button
                className="re-btn success"
                onClick={learnAllBatch}
                disabled={batchRunning || batchQueue.every((it) => it.learnResult || it.status !== "done")}
              >
                Alle lernen
              </button>
              <button className="re-btn secondary" onClick={() => setBatchQueue([])} disabled={batchRunning}>
                Leeren
              </button>
            </div>
          </div>
          <table className="re-table re-batch-table">
            <thead>
              <tr><th>Datei</th><th>Dateiname (bearbeitbar)</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {batchQueue.map((item, idx) => (
                <tr key={item.file.path_lower} className={item.learnResult ? "re-batch-done" : ""}>
                  <td className="re-field" title={item.file.path_display || item.file.path_lower}>
                    {item.file.name}
                  </td>
                  <td>
                    {item.status === "done" ? (
                      <input
                        className="re-input re-batch-name"
                        value={item.targetName}
                        placeholder="PREFIX - YYYY-MM-DD - Absender.pdf"
                        onChange={(e) =>
                          setBatchQueue((q) => q.map((it, i) =>
                            i === idx ? { ...it, targetName: e.target.value } : it))
                        }
                      />
                    ) : item.status === "analyzing" ? (
                      <span className="re-muted">analysiere…</span>
                    ) : item.status === "error" ? (
                      <span className="re-conf bad" title={item.learnError}>{item.learnError?.slice(0, 60)}</span>
                    ) : (
                      <span className="re-muted">ausstehend</span>
                    )}
                  </td>
                  <td>
                    {item.learnResult ? (
                      <span className="re-conf ok">
                        ✓ gelernt ({item.learnResult.learned?.length || 0} Anker)
                      </span>
                    ) : item.status === "analyzing" ? (
                      <span className="re-muted">…</span>
                    ) : item.status === "error" ? (
                      <span className="re-conf bad">✗</span>
                    ) : null}
                  </td>
                  <td>
                    {item.status === "done" && !item.learnResult && (
                      <button
                        className="re-btn success"
                        disabled={item.learning}
                        onClick={() => learnBatchItem(item, idx)}
                        style={{ padding: "3px 10px", fontSize: 12 }}
                      >
                        {item.learning ? "lerne…" : "Lernen"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* ── ERWEITERTE OPTIONEN (eingeklappt) ── */}
      <section className="re-card re-advanced">
        <button className="re-advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "▾" : "▸"} Erweiterte Optionen — Status, Kataloge, Ranker-Training
          {goldStats?.count > 0 && (
            <span className="re-badge" style={{ marginLeft: 8, fontSize: 11 }}>
              {goldStats.count} Gold-Dok.
            </span>
          )}
        </button>
        {showAdvanced && (
          <div className="re-grid" style={{ marginTop: 16 }}>
            <div>
              <h2 style={{ fontSize: 15, marginBottom: 10 }}>Backend-Status</h2>
              {status ? (
                <ul className="re-kv">
                  <li><span>Version</span><b>{status.version}</b></li>
                  <li>
                    <span>Datenbank</span>
                    <b className={status.db_connected ? "ok" : "bad"}>
                      {status.db_connected ? "verbunden" : "getrennt"}
                    </b>
                  </li>
                  <li>
                    <span>Dropbox</span>
                    <b className={status.dropbox_available ? "ok" : "bad"}>
                      {status.dropbox_available ? "verfügbar" : "n/a"}
                    </b>
                  </li>
                  <li><span>Gold-Datensätze</span><b>{goldStats ? goldStats.count : "—"}</b></li>
                </ul>
              ) : (
                <p className="re-muted">lädt…</p>
              )}
              <div className="re-row">
                <button className="re-btn secondary" onClick={loadStatus}>Aktualisieren</button>
                <button
                  className="re-btn success"
                  onClick={runTraining}
                  disabled={training || !goldStats || goldStats.count < 1}
                  title={!goldStats || goldStats.count < 1 ? "Erst Gold-Werte speichern" : "Ranker neu trainieren"}
                >
                  {training ? "trainiere…" : `Ranker trainieren (${goldStats?.count || 0} Dok.)`}
                </button>
              </div>
              {trainError && <div className="re-alert re-alert-error">{trainError}</div>}
              {trainResult && (
                <div className="re-alert re-alert-success">
                  Training fertig.{" "}
                  {trainResult.persisted?.version
                    ? `v${trainResult.persisted.version}, ${trainResult.persisted.rows} Gewichte`
                    : trainResult.error || "Keine neuen Gewichte (zu wenig Stichproben)."}
                </div>
              )}
            </div>
            <div>
              <h2 style={{ fontSize: 15, marginBottom: 10 }}>Stammdaten (read-only)</h2>
              {catalogs ? (
                <ul className="re-kv">
                  {Object.entries(catalogs).map(([k, v]) => (
                    <li key={k}><span>{k}</span><b>{v}</b></li>
                  ))}
                </ul>
              ) : (
                <p className="re-muted">lädt…</p>
              )}
            </div>
          </div>
        )}
      </section>

    </div>
  );
}

